/**
 * @module data/governance
 *
 * The governance model: who decides what, in which forum, at what cadence, and
 * what happens when the answer is no.
 *
 * Governance is presented as four interlocking parts:
 *
 *  - **Forums** — the meetings that actually hold decision rights.
 *  - **Decision rights** — a RACI-style allocation across the ADM.
 *  - **Controls** — the guardrails that make compliance measurable.
 *  - **Cadence** — the rhythm, from weekly to annual.
 */

/**
 * @typedef {Object} Forum
 * @property {string}   id
 * @property {string}   name
 * @property {string}   cadence
 * @property {string}   mandate       What it exists to decide.
 * @property {string[]} members
 * @property {string}   decides       The decision it owns outright.
 * @property {string}   escalates     What it must push upward.
 * @property {string}   accent
 */

/** @type {Forum[]} */
export const FORUMS = [
  {
    id: "architecture-board",
    name: "Architecture Board",
    cadence: "Fortnightly",
    mandate:
      "Owns the architecture baseline. Reviews designs against the target, grants or refuses dispensations and controls change to the baseline.",
    members: ["Chief Architect (chair)", "Domain architects", "Security", "Delivery leads"],
    decides: "Compliance, dispensations and changes to the architecture baseline",
    escalates: "Scope, funding and conflicting business priorities",
    accent: "#e0b584",
  },
  {
    id: "ai-review-board",
    name: "AI Review Board",
    cadence: "Fortnightly",
    mandate:
      "Assesses every AI service before and after deployment: evaluation evidence, fairness, explainability, cost envelope and human oversight.",
    members: ["Responsible AI lead (chair)", "Data protection", "Security", "Business owner"],
    decides: "Approval, conditions and suspension of AI services in production",
    escalates: "Residual risk accepted outside tolerance to the executive sponsor",
    accent: "#5fd4c4",
  },
  {
    id: "change-council",
    name: "Transformation Council",
    cadence: "Monthly",
    mandate:
      "Holds the portfolio. Sequences work packages against value, risk and the organisation's capacity to absorb change.",
    members: ["Executive sponsor (chair)", "Business owners", "Finance", "Chief Architect", "Change lead"],
    decides: "Portfolio sequencing, funding tranches and benefit-case changes",
    escalates: "Strategic reprioritisation and investment above delegated limits",
    accent: "#d8a657",
  },
  {
    id: "value-office",
    name: "Value Office",
    cadence: "Monthly",
    mandate:
      "Keeps the value case honest: tracks realisation against the baseline, reconciles benefits and publishes the metric tree.",
    members: ["Value lead", "Finance business partner", "Measurement analyst", "Capability owners"],
    decides: "Benefit measurement definitions and realisation reporting",
    escalates: "Material variance between realised and forecast value",
    accent: "#9db08a",
  },
];

/**
 * Decision rights by ADM phase. Rows are activities, columns are roles.
 *
 * `R` responsible · `A` accountable · `C` consulted · `I` informed
 *
 * @typedef {Object} DecisionRight
 * @property {string} activity
 * @property {Record<string, string>} roles
 */

/** The role columns, in display order. */
export const DECISION_ROLES = [
  "Architecture Board",
  "AI Review Board",
  "Transformation Council",
  "Value Office",
  "Delivery Team",
];

/** @type {DecisionRight[]} */
export const DECISION_RIGHTS = [
  {
    activity: "Architecture principles & method tailoring",
    roles: { "Architecture Board": "A", "AI Review Board": "C", "Transformation Council": "I", "Value Office": "I", "Delivery Team": "C" },
  },
  {
    activity: "Architecture vision & value case",
    roles: { "Architecture Board": "C", "AI Review Board": "C", "Transformation Council": "A", "Value Office": "C", "Delivery Team": "R" },
  },
  {
    activity: "Data & model contracts",
    roles: { "Architecture Board": "A", "AI Review Board": "C", "Transformation Council": "I", "Value Office": "I", "Delivery Team": "R" },
  },
  {
    activity: "Agent deployment & autonomy level",
    roles: { "Architecture Board": "C", "AI Review Board": "A", "Transformation Council": "I", "Value Office": "I", "Delivery Team": "R" },
  },
  {
    activity: "Work package sequencing & funding",
    roles: { "Architecture Board": "C", "AI Review Board": "I", "Transformation Council": "A", "Value Office": "C", "Delivery Team": "C" },
  },
  {
    activity: "Dispensation & design deviation",
    roles: { "Architecture Board": "A", "AI Review Board": "C", "Transformation Council": "I", "Value Office": "I", "Delivery Team": "R" },
  },
  {
    activity: "Benefit realisation & metric tree",
    roles: { "Architecture Board": "I", "AI Review Board": "I", "Transformation Council": "C", "Value Office": "A", "Delivery Team": "R" },
  },
  {
    activity: "Architecture change & re-architecture",
    roles: { "Architecture Board": "R", "AI Review Board": "C", "Transformation Council": "A", "Value Office": "C", "Delivery Team": "C" },
  },
];

/**
 * The controls that make governance measurable rather than rhetorical.
 *
 * @typedef {Object} GovernanceControl
 * @property {string} id
 * @property {string} name
 * @property {string} body
 * @property {string} evidence  What exists if the control is real.
 * @property {string} glyph
 */

/** @type {GovernanceControl[]} */
export const CONTROLS = [
  {
    id: "gate",
    name: "Phase gates",
    body: "Each ADM phase closes on a named gate with defined evidence. No gate, no next phase — and no informal 'we'll document it later'.",
    evidence: "Signed gate record with evidence links in the architecture repository.",
    glyph: "check",
  },
  {
    id: "traceability",
    name: "Requirement traceability",
    body: "Every requirement traces forward to a decision, a capability and an owner, and backward to the driver that produced it.",
    evidence: "A traceability matrix with zero orphaned requirements.",
    glyph: "gantt",
  },
  {
    id: "dispensation",
    name: "Dispensation register",
    body: "Exceptions are legitimate, but they are recorded, time-bound and owned — never silent.",
    evidence: "A register where every open item has an expiry date and a remediation plan.",
    glyph: "book",
  },
  {
    id: "policy-as-code",
    name: "Policy as code",
    body: "Architecture rules are encoded as automated checks that run in the delivery pipeline, so compliance is observed continuously.",
    evidence: "Pipeline checks failing builds that breach a baseline rule.",
    glyph: "sliders",
  },
  {
    id: "evaluation",
    name: "Evidence-based release",
    body: "No AI service reaches production without evaluation results, a human-oversight plan and a rollback path.",
    evidence: "A release record carrying its evaluation report and oversight plan.",
    glyph: "target",
  },
  {
    id: "value",
    name: "Value reconciliation",
    body: "Realised benefit is compared with the original case on a published cadence, and the variance is explained rather than hidden.",
    evidence: "A value dashboard reconciled to the finance ledger.",
    glyph: "pulse",
  },
];

/**
 * The governance rhythm.
 *
 * @typedef {Object} CadenceItem
 * @property {string} period
 * @property {string} forum
 * @property {string} output
 */

/** @type {CadenceItem[]} */
export const CADENCE = [
  { period: "Weekly", forum: "Delivery stand-up & architecture clinic", output: "Blockers resolved, design questions answered within the week." },
  { period: "Fortnightly", forum: "Architecture Board & AI Review Board", output: "Compliance decisions, dispensations, AI service approvals." },
  { period: "Monthly", forum: "Transformation Council & Value Office", output: "Portfolio sequencing, funding tranches, value reconciliation." },
  { period: "Quarterly", forum: "Architecture change review", output: "Change radar, drift assessment, cycle recalibration." },
  { period: "Annually", forum: "Capability & method retrospective", output: "Updated principles, tailored ADM, next-cycle hypotheses." },
];

/** Section copy. */
export const GOVERNANCE_SUMMARY = {
  eyebrow: "Governance",
  title: "Governance is the method's spine",
  lede:
    "A transformation without governance is a series of unrelated purchases. We stand up four forums with real decision rights, encode the rules as automated controls, and publish the rhythm so everyone knows when a decision will be made.",
};
